"use client";

import { useRef, useState } from "react";
import { Share2, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import type { ScoreResult } from "@/lib/scoring/types";
import type { SecurityRef } from "@/lib/data/types";
import { SITE_URL } from "@/lib/config";

const SITE_DOMAIN = SITE_URL.replace(/^https?:\/\//, "");

const RING_SIZE = 180;
const RING_STROKE = 14;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Eigene, unanimierte Ring-Variante nur fuer den Export: feste Hex-Farben statt
 * CSS-Variablen, damit die exportierte Grafik immer gleich aussieht, unabhaengig
 * vom Hell/Dunkel-Modus der Seite, in der der Dialog gerade angezeigt wird.
 * `variant="dark"` fuer das Story-Format (dunkler Königsblau-Hintergrund) - nur
 * Track-/Text-Farbe wechseln, der Navy->Gold-Ring-Gradient bleibt gleich. Exportiert
 * (statt nur lokal in dieser Datei) - components/portfolio/depot-score-card-export.tsx
 * nutzt exakt dieselbe Ring-Darstellung fuer den Depot-Gesamt-Score, keine zweite,
 * potenziell divergierende Kopie. */
export function ExportScoreRing({ score, variant = "light" }: { score: number; variant?: "light" | "dark" }) {
  const clamped = Math.max(0, Math.min(100, score));
  const offset = RING_CIRCUMFERENCE * (1 - clamped / 100);
  const trackColor = variant === "dark" ? "#26407F" : "#ECEAE3";
  const textColor = variant === "dark" ? "#FAF9F5" : "#16265C";
  const subTextColor = variant === "dark" ? "#9DB0D6" : "#6B7280";
  return (
    <div className="relative shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
      <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}>
        <defs>
          <linearGradient id="exportRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#16265C" />
            <stop offset="100%" stopColor="#C6A15B" />
          </linearGradient>
        </defs>
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke={trackColor}
          strokeWidth={RING_STROKE}
        />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke="url(#exportRingGradient)"
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="flex items-baseline gap-1">
          <span className="font-serif text-4xl" style={{ color: textColor }}>
            {Math.round(clamped)}
          </span>
          <span className="text-sm" style={{ color: subTextColor }}>
            / 100
          </span>
        </div>
      </div>
    </div>
  );
}

type ExportFormat = "card" | "story";

export function ScoreCardExport({
  securityRef,
  score,
}: {
  securityRef: SecurityRef;
  score: ScoreResult;
}) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<ExportFormat>("card");
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  async function handleDownload() {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // Dynamischer statt Top-Level-Import, siehe chart-card.tsx fuer die Begruendung.
      const { toPng } = await import("html-to-image");
      if (document.fonts?.ready) await document.fonts.ready;
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true });
      const link = document.createElement("a");
      const suffix = format === "story" ? "-story" : "";
      link.download = `erikonomie-score-${securityRef.symbol}${suffix}.png`;
      link.href = dataUrl;
      link.click();
    } catch {
      // Reine Komfortfunktion - bei Fehlschlag bleibt der Dialog offen statt abzustuerzen.
    } finally {
      setDownloading(false);
    }
  }

  const isStory = format === "story";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5">
        <Share2 className="h-3.5 w-3.5" />
        Score-Karte teilen
      </Button>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Score-Karte</DialogTitle>
          <DialogDescription>Als Bild herunterladen und teilen.</DialogDescription>
        </DialogHeader>

        <div className="flex justify-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={isStory ? "ghost" : "secondary"}
            onClick={() => setFormat("card")}
          >
            Karte
          </Button>
          <Button
            type="button"
            size="sm"
            variant={isStory ? "secondary" : "ghost"}
            onClick={() => setFormat("story")}
          >
            Story (9:16)
          </Button>
        </div>

        <div className="flex justify-center">
          {isStory ? (
            <div
              ref={cardRef}
              className="flex w-[360px] flex-col items-center gap-6 rounded-3xl p-9"
              style={{ backgroundColor: "#16265C", aspectRatio: "9 / 16" }}
            >
              <div className="flex flex-col items-center gap-2 pt-2">
                <img src="/logo.png" alt="" width={72} height={72} style={{ height: 72, width: 72 }} />
                <span className="wordmark text-sm uppercase" style={{ color: "#E8C784" }}>
                  Erikonomie
                </span>
              </div>

              <div className="flex flex-1 flex-col items-center justify-center gap-6">
                <div className="flex flex-col items-center gap-0.5 text-center">
                  <span className="font-serif text-xl" style={{ color: "#FAF9F5" }}>
                    {securityRef.name}
                  </span>
                  <span className="text-xs tracking-wide uppercase" style={{ color: "#9DB0D6" }}>
                    {securityRef.symbol}
                  </span>
                </div>

                <ExportScoreRing score={score.total} variant="dark" />

                <p className="text-center text-sm leading-snug" style={{ color: "#DCE3F5" }}>
                  {score.summary}
                </p>
              </div>

              <div
                className="w-full pt-4 text-center text-[0.65rem] leading-relaxed"
                style={{ borderTop: "1px solid #26407F", color: "#9DB0D6" }}
              >
                Datenqualität {score.dataQuality}/10 Bereiche · Bildungswerkzeug, keine
                Anlageberatung
                <br />
                {SITE_DOMAIN}
              </div>
            </div>
          ) : (
            <div
              ref={cardRef}
              className="flex w-[360px] flex-col items-center gap-5 rounded-3xl p-8"
              style={{ backgroundColor: "#FAF9F5", border: "1px solid #ECEAE3" }}
            >
              <div className="flex items-center gap-2">
                {/* Direktes <img> statt next/image: html-to-image rastert den
                    Node roh, next/image's optimierte /_next/image-URL macht die
                    Erfassung nur unnoetig fragiler. */}
                <img src="/logo.png" alt="" width={24} height={24} style={{ height: 24, width: 24 }} />
                <span className="wordmark text-sm uppercase" style={{ color: "#16265C" }}>
                  Erikonomie
                </span>
              </div>

              <div className="flex flex-col items-center gap-0.5 text-center">
                <span className="font-serif text-lg" style={{ color: "#16265C" }}>
                  {securityRef.name}
                </span>
                <span className="text-xs tracking-wide uppercase" style={{ color: "#6B7280" }}>
                  {securityRef.symbol}
                </span>
              </div>

              <ExportScoreRing score={score.total} />

              <p className="text-center text-sm leading-snug" style={{ color: "#26407F" }}>
                {score.summary}
              </p>

              <div
                className="w-full pt-4 text-center text-[0.65rem] leading-relaxed"
                style={{ borderTop: "1px solid #ECEAE3", color: "#6B7280" }}
              >
                Datenqualität {score.dataQuality}/10 Bereiche · Bildungswerkzeug, keine
                Anlageberatung
                <br />
                {SITE_DOMAIN}
              </div>
            </div>
          )}
        </div>

        <Button type="button" onClick={handleDownload} disabled={downloading} className="gap-1.5">
          {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Als PNG herunterladen
        </Button>
      </DialogContent>
    </Dialog>
  );
}
